/**
 * The canonical world clock — the map between wall time and world time that a
 * hosted world is the authority for.
 *
 * `sim/clock.ts` is the *client's* clock: a `WorldClock` that only moves when
 * somebody calls `advance(dtReal)`, so a shut tab freezes it. That is exactly
 * right for a renderer and exactly wrong for a world that is supposed to go on
 * without anybody. This file is the other one. It holds no time of its own; it
 * holds an **anchor**, and world time is derived from the anchor and whatever
 * the wall clock happens to say:
 *
 *     worldTime(now) = anchorWorldTime + (now − anchorRealMs)/1000 × timeScale
 *
 * Three properties fall out of storing an anchor rather than a counter, and
 * all three are the reason for it:
 *
 * - **The world advances across downtime.** A process that was off for six
 *   hours boots, reads the same anchor it wrote, and finds the world owes it
 *   six hours of history. Nothing has to have been running.
 * - **Re-anchoring at the resolved present is a no-op.** `reanchor(s, now)`
 *   sets `anchorWorldTime = worldTimeAt(s, now)` and `anchorRealMs = now`, and
 *   every future reading is unchanged. That is what makes pause, resume and a
 *   time-scale change safe: each of them resolves *first*, then writes, so the
 *   clock never jumps. It is also why a checkpoint may rewrite the anchor
 *   freely — as long as the world really is caught up to it (see `lagOf`).
 * - **It is data, not an object.** `WorldClockState` is a flat JSON record, so
 *   it survives a restart in the world manifest without a serializer.
 *
 * ## What this module refuses to do
 *
 * It never reads the wall clock. Every function takes `nowMs`. That is the
 * same discipline `sim/persistence/catchup.ts` keeps and for the same reason:
 * the one genuinely non-reproducible input in the system should enter through
 * a parameter, at one place the operator can see, rather than being sampled
 * wherever it is convenient. The server samples `Date.now()`; this file is
 * testable without one.
 *
 * And it never lets the world go backwards. A wall clock that steps back — an
 * NTP correction, a timezone change, a hand-edited manifest — reads as zero
 * elapsed, never as negative history. `sim/persistence/catchup.ts` takes the
 * same position on the same hazard from the client side.
 */

import { CANON_SPEED, DAY, YEAR_DAYS } from '@/sim/clock';

/**
 * The whole real↔world mapping, as it is persisted.
 *
 * Deliberately NOT part of `SimulationSnapshot`. When a world was last hosted
 * is not world history, and a snapshot carrying it would stop being a pure
 * function of seed and duration — the same argument `main.ts` makes for
 * keeping its wall-clock stamp beside the save rather than inside it. This
 * lives in the world manifest, where an operator can read and edit it, and a
 * fork gets a fresh copy.
 */
export interface WorldClockState {
  /** Epoch milliseconds at which `anchorWorldTime` was the truth. */
  anchorRealMs: number;
  /** Canonical world-seconds at the anchor. */
  anchorWorldTime: number;
  /** World-seconds per real second. `CANON_SPEED` unless an operator moved it. */
  timeScale: number;
  /**
   * An ADMIN pause: world time holds at `anchorWorldTime` however much real
   * time passes. This is maintenance, not a game feature — law 27 says the
   * shared world has one clock and no player may stop it — so it exists only
   * behind the CLI and it is recorded when it is used.
   */
  paused: boolean;
  /** Why, for the operator reading `world status` three weeks later. */
  pauseReason?: string;
}

/** A world that has never been hosted: its clock starts now, at its own time. */
export function newClockState(
  nowMs: number,
  worldTime: number,
  timeScale: number = CANON_SPEED,
): WorldClockState {
  return {
    anchorRealMs: nowMs,
    anchorWorldTime: worldTime,
    timeScale: saneScale(timeScale),
    paused: false,
  };
}

/**
 * A time scale that cannot produce a non-finite or rewinding world.
 *
 * Zero is refused rather than clamped-to-zero-and-allowed: a scale of zero is
 * a pause wearing a different hat, and a pause has its own flag that says so
 * in `world status`. Two ways to express one state is how an operator ends up
 * unable to explain why the world is not moving.
 */
export function saneScale(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 0) return CANON_SPEED;
  return scale;
}

/**
 * Canonical world time at wall-clock instant `nowMs`.
 *
 * This is the whole contract of the file. Everything else re-writes anchors so
 * that this function keeps returning something sensible.
 */
export function worldTimeAt(s: WorldClockState, nowMs: number): number {
  if (!Number.isFinite(s.anchorWorldTime)) return 0;
  if (s.paused) return s.anchorWorldTime;
  const elapsedMs = Number.isFinite(nowMs) ? nowMs - s.anchorRealMs : 0;
  // A clock that went backwards reads as zero elapsed. The world may stall;
  // it may never rewind, because history that has been simulated is history.
  const elapsedReal = Math.max(0, elapsedMs) / 1000;
  return s.anchorWorldTime + elapsedReal * saneScale(s.timeScale);
}

/**
 * Move the anchor to the present without changing the mapping.
 *
 * **Only correct when the world is actually caught up.** Re-anchoring while
 * the simulation is behind `worldTimeAt` forgives the lag silently — the debt
 * simply stops existing — which is the one way this module could quietly lose
 * history. `HostedWorld` re-anchors after a completed catch-up and refuses to
 * after a capped one; `lagOf` is how it tells the difference.
 */
export function reanchor(s: WorldClockState, nowMs: number): WorldClockState {
  return { ...s, anchorWorldTime: worldTimeAt(s, nowMs), anchorRealMs: nowMs };
}

/** Freeze world time here. Resolves first, so the instant is exact. */
export function pause(s: WorldClockState, nowMs: number, reason?: string): WorldClockState {
  if (s.paused) return reason === undefined ? s : { ...s, pauseReason: reason };
  const at = reanchor(s, nowMs);
  return { ...at, paused: true, ...(reason === undefined ? {} : { pauseReason: reason }) };
}

/**
 * Start counting again from now. The paused span is simply not world time:
 * a world paused for maintenance for an hour is an hour younger than a world
 * that was not, which is the honest reading of a deliberate freeze.
 */
export function resume(s: WorldClockState, nowMs: number): WorldClockState {
  if (!s.paused) return s;
  const { pauseReason: _drop, ...rest } = s;
  return { ...rest, anchorRealMs: nowMs, paused: false };
}

/**
 * Change how fast the world runs, without moving it.
 *
 * Resolve, then write. Doing it the other way round re-interprets all the real
 * time since the last anchor at the new rate, which teleports the world by
 * however long that was × the difference — a decade, if the anchor was a week
 * old and somebody doubled the scale.
 */
export function setTimeScale(
  s: WorldClockState, nowMs: number, scale: number,
): WorldClockState {
  const at = reanchor(s, nowMs);
  return { ...at, timeScale: saneScale(scale) };
}

/**
 * How far behind its own clock a simulation is, in world-seconds. Zero when
 * caught up; never negative from the caller's point of view — a simulation
 * that is somehow AHEAD of its clock is reported through `aheadOf` instead, so
 * the two conditions cannot be confused for one another by a sign.
 */
export function lagOf(s: WorldClockState, nowMs: number, simWorldTime: number): number {
  return Math.max(0, worldTimeAt(s, nowMs) - simWorldTime);
}

/**
 * How far a simulation has run PAST its own clock, in world-seconds.
 *
 * Not a theoretical case: it is what a restored checkpoint looks like when the
 * time scale was lowered after it was taken, and what any hand-advanced world
 * looks like. The world may not rewind, so the only correct response is to
 * re-anchor forward — `HostedWorld.open` does, and says so.
 */
export function aheadOf(s: WorldClockState, nowMs: number, simWorldTime: number): number {
  return Math.max(0, simWorldTime - worldTimeAt(s, nowMs));
}

/** Pin the anchor to a world time the simulation has actually reached. */
export function anchorAtWorldTime(
  s: WorldClockState, nowMs: number, worldTime: number,
): WorldClockState {
  return { ...s, anchorRealMs: nowMs, anchorWorldTime: worldTime };
}

/**
 * Real seconds one world-second costs at this scale — the number an operator
 * needs to answer "how long will that take", and the number a `--catchup-cap`
 * is really about.
 */
export const realSecondsPerWorldSecond = (s: WorldClockState): number =>
  1 / saneScale(s.timeScale);

/** `year 114 · day 37 · 14:26` — the same calendar `WorldClock` prints. */
export function worldTimeText(worldTime: number): string {
  const t = Math.max(0, worldTime);
  const day = Math.floor(t / DAY);
  const s = t % DAY;
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  return `year ${Math.floor(day / YEAR_DAYS)} · day ${(day % YEAR_DAYS) + 1} · ${hh}:${mm}`;
}

/** `3.7 world-years`, `192 world-days`, `8 world-hours` — a span, read aloud. */
export function worldSpanText(worldSeconds: number): string {
  const s = Math.max(0, worldSeconds);
  const years = s / (DAY * YEAR_DAYS);
  if (years >= 1) return `${years.toFixed(years >= 10 ? 0 : 1)} world-years`;
  const days = s / DAY;
  if (days >= 1) return `${days.toFixed(days >= 10 ? 0 : 1)} world-days`;
  return `${(s / 3600).toFixed(1)} world-hours`;
}
